import { ListItem, Typography, Checkbox, Stack, Tooltip, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/EditOutlined';
import MergeIcon from '@mui/icons-material/Merge';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useArchiveItem } from './Hooks/useArchiveItem';
import { cellColors } from '../../shared/constants/commonCellColors';
import isTargetMergingItem from '../../shared/functions/isSelectedContact';
import { IssueAndServiceSharedValues } from '../../shared/constants/IssueAndServiceKeys';
import { TYPE_TOOLTIP_PLACEHOLDER } from '../../shared/constants/tableTooltips';

const EnumListTypeItem = ({
  targetMergingItem,
  enumItem,
  isMerging,
  selectedItems,
  handleCheckboxChange,
  setIsEditing,
  setSelectedEnum,
  setEnumModalOpen,
  setIsMerging,
  isArchivedItem,
  typesImportantForAnalytics,
  whatIsBeingMergedForTypes,
  setWhatIsBeingMergedForTypes,
}) => {
  const archiveItemMutation = useArchiveItem(enumItem, IssueAndServiceSharedValues.type);

  const handleArchiveClick = async () => {
    await archiveItemMutation.mutateAsync();
  };

  // References the choices made in client\src\shared\functions\cellStyling.jsx
  const typeBackgroundColorOptionsToCellColor = {
    Green: cellColors.support.background,
    Red: cellColors.issue.background,
  };

  const shouldBeDisabled = isTargetMergingItem(targetMergingItem, enumItem);
  const trimmedTooltip = typeof enumItem.tooltip === 'string' ? enumItem.tooltip.trim() : '';
  const typeTooltipTitle = (
    <>
      <Typography variant="body2">{trimmedTooltip || TYPE_TOOLTIP_PLACEHOLDER}</Typography>
      {typesImportantForAnalytics.includes(enumItem.name) && (
        <Typography variant="body2">
          {'This type is important for analytics. Some editing restrictions are in place for this type so that it is irremovable.'}
        </Typography>
      )}
    </>
  );

  return (
    <ListItem
      divider={true}
      sx={{
        alignContent: 'space-between',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: typeBackgroundColorOptionsToCellColor[enumItem.backgroundColor],
      }}
      disabled={shouldBeDisabled}
    >
      <Tooltip title={typeTooltipTitle} placement="right">
        <Stack direction={'column'}>
          {typesImportantForAnalytics.includes(enumItem.name) ? (
            <Typography sx={{ color: 'primaryDarker.main' }}>{enumItem.name}</Typography>
          ) : (
            <Typography>{enumItem.name}</Typography>
          )}
          <Typography sx={{ opacity: 0.6 }}>{enumItem.proactivity ?? ''}</Typography>
        </Stack>
      </Tooltip>

      {isMerging ? (
        whatIsBeingMergedForTypes === 'Types' && (
          <Checkbox
            disabled={typesImportantForAnalytics.includes(enumItem.name) || shouldBeDisabled}
            checked={selectedItems.has(enumItem)}
            onChange={(event) => handleCheckboxChange(event, enumItem)}
          />
        )
      ) : (
        <Stack direction={'row'} sx={{ alignContent: 'center', alignItems: 'center', marginLeft: '5%' }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => {
                setIsEditing(true);
                setSelectedEnum(enumItem);
                setEnumModalOpen(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Merge Types">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedEnum(enumItem);
                setWhatIsBeingMergedForTypes('Types');
                setIsMerging(true);
              }}
            >
              <MergeIcon />
            </IconButton>
          </Tooltip>

          {isArchivedItem ? (
            <Tooltip title="Unarchive">
              <IconButton size="small" onClick={handleArchiveClick}>
                <UnarchiveIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Archive">
              <IconButton size="small" onClick={handleArchiveClick}>
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </ListItem>
  );
};

EnumListTypeItem.propTypes = {
  targetMergingItem: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  enumItem: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string,
    tooltip: PropTypes.string,
    proactivity: PropTypes.string,
  }).isRequired,
  isMerging: PropTypes.bool.isRequired,
  selectedItems: PropTypes.instanceOf(Set).isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  setSelectedEnum: PropTypes.func.isRequired,
  setEnumModalOpen: PropTypes.func.isRequired,
  setIsMerging: PropTypes.func.isRequired,
  isArchivedItem: PropTypes.bool,
  typesImportantForAnalytics: PropTypes.arrayOf(PropTypes.string).isRequired,
  whatIsBeingMergedForTypes: PropTypes.string.isRequired,
  setWhatIsBeingMergedForTypes: PropTypes.func.isRequired,
};

EnumListTypeItem.defaultProps = {
  targetMergingItem: null,
  isArchivedItem: false,
};

export default EnumListTypeItem;
